import { useState, useEffect } from 'react';
import { userSession } from '../utils/userSession.js';
import ImageUrlField from './ImageUrlField.js';
import DashboardModal from './DashboardModal.js';
import FormTabs, { FormTabPanel } from './FormTabs.js';

const TABS = [
  { id: 'business', label: 'Business' },
  { id: 'contact', label: 'Contact' }
];

export default function EditPartner({ isOpen, onClose, onPartnerUpdated, partnerData, partnerId }) {
  const [tab, setTab] = useState('business');
  const [formData, setFormData] = useState({
    partnerName: partnerData?.PartnerName || '',
    partnerContact: partnerData?.PartnerContact || '',
    partnerEmail: partnerData?.PartnerEmail || '',
    partnerPhone: partnerData?.PartnerPhone || '',
    partnerWebsiteLink: partnerData?.PartnerWebsiteLink || '',
    partnerPublicEmail: partnerData?.PartnerPublicEmail || '',
    partnerLogo: partnerData?.PartnerLogo || '',
    partnerObjLink: partnerData?.PartnerObjLink || '',
    partnerObjImage: partnerData?.PartnerObjImage || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (partnerData) {
      setFormData({
        partnerName: partnerData.PartnerName || '',
        partnerContact: partnerData.PartnerContact || '',
        partnerEmail: partnerData.PartnerEmail || '',
        partnerPhone: partnerData.PartnerPhone || '',
        partnerWebsiteLink: partnerData.PartnerWebsiteLink || '',
        partnerPublicEmail: partnerData.PartnerPublicEmail || '',
        partnerLogo: partnerData.PartnerLogo || '',
        partnerObjLink: partnerData.PartnerObjLink || '',
        partnerObjImage: partnerData.PartnerObjImage || ''
      });
    }
    if (isOpen) {
      setTab('business');
      setError('');
    }
  }, [partnerData, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(
        `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/partners/${partnerData.PartnerID}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            userId: userSession.getUserId()
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update partner');
      }

      if (onPartnerUpdated) {
        onPartnerUpdated();
      }
      onClose();
    } catch (err) {
      setError('Failed to update partner. Please try again.');
      console.error('Error updating partner:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <DashboardModal
      title="Edit Partner Details"
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      onRevealTab={setTab}
      tabs={<FormTabs tabs={TABS} active={tab} onChange={setTab} />}
    >
      <FormTabPanel id="business" active={tab}>
        <div>
          <label htmlFor="partnerName" className="block text-sm font-medium text-gray-700">
            Business Name *
          </label>
          <input
            type="text"
            id="partnerName"
            name="partnerName"
            required
            value={formData.partnerName}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerPublicEmail" className="block text-sm font-medium text-gray-700">
            Public Email
          </label>
          <input
            type="email"
            id="partnerPublicEmail"
            name="partnerPublicEmail"
            value={formData.partnerPublicEmail}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerWebsiteLink" className="block text-sm font-medium text-gray-700">
            Website Link
          </label>
          <input
            type="url"
            id="partnerWebsiteLink"
            name="partnerWebsiteLink"
            value={formData.partnerWebsiteLink}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <ImageUrlField
          id="partnerLogo"
          name="partnerLogo"
          label="Logo URL"
          type="PartnerLogo"
          entityId={partnerData?.PartnerID || partnerId}
          value={formData.partnerLogo}
          onChange={(partnerLogo) => setFormData((prev) => ({ ...prev, partnerLogo }))}
        />

        <div>
          <label htmlFor="partnerObjLink" className="block text-sm font-medium text-gray-700">
            Banner Web Link
          </label>
          <input
            type="url"
            id="partnerObjLink"
            name="partnerObjLink"
            value={formData.partnerObjLink}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <ImageUrlField
          id="partnerObjImage"
          name="partnerObjImage"
          label="Banner Image URL (900x150px)"
          type="PartnerObjImage"
          entityId={partnerData?.PartnerID || partnerId}
          value={formData.partnerObjImage}
          onChange={(partnerObjImage) => setFormData((prev) => ({ ...prev, partnerObjImage }))}
        />
      </FormTabPanel>

      <FormTabPanel id="contact" active={tab}>
        <div>
          <label htmlFor="partnerContact" className="block text-sm font-medium text-gray-700">
            Contact Name *
          </label>
          <input
            type="text"
            id="partnerContact"
            name="partnerContact"
            required
            value={formData.partnerContact}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerPhone" className="block text-sm font-medium text-gray-700">
            Contact Phone *
          </label>
          <input
            type="tel"
            id="partnerPhone"
            name="partnerPhone"
            required
            value={formData.partnerPhone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerEmail" className="block text-sm font-medium text-gray-700">
            Contact Email *
          </label>
          <input
            type="email"
            id="partnerEmail"
            name="partnerEmail"
            required
            value={formData.partnerEmail}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>
      </FormTabPanel>
    </DashboardModal>
  );
}
